import { useEffect, useState } from "react";

// The per-browser demo toggles, flipped from the Super Admin → Demos submenu.
// Each flag lives in localStorage under demo.<name>.enabled and syncs across
// components (CustomEvent) and tabs (storage event).

function createDemoFlag(name: string): () => [boolean, (next: boolean) => void] {
    const STORAGE_KEY = `demo.${name}.enabled`;
    const EVENT_NAME = `demo.${name}.change`;

    const readFlag = (): boolean => {
        try {
            return localStorage.getItem(STORAGE_KEY) === "1";
        } catch {
            return false;
        }
    };

    const writeFlag = (value: boolean) => {
        try {
            if (value) {
                localStorage.setItem(STORAGE_KEY, "1");
            } else {
                localStorage.removeItem(STORAGE_KEY);
            }
        } catch {
            // localStorage unavailable; nothing to persist
        }
        window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: value }));
    };

    return function useDemoFlag(): [boolean, (next: boolean) => void] {
        const [enabled, setEnabled] = useState<boolean>(readFlag);

        useEffect(() => {
            const onChange = () => setEnabled(readFlag());
            window.addEventListener(EVENT_NAME, onChange);
            window.addEventListener("storage", onChange); // cross-tab
            return () => {
                window.removeEventListener(EVENT_NAME, onChange);
                window.removeEventListener("storage", onChange);
            };
        }, []);

        return [enabled, writeFlag];
    };
}

/** Reveals the Budget demo entry in the main Oasis nav. */
export const useBudgetDemoFlag = createDemoFlag("budget");

/**
 * The AI chat demo: guided intake and the analysis assistant play scripted
 * responses instead of calling the AI backend, so a presenter can click
 * through a deal without typing or dictating.
 */
export const useChatDemoFlag = createDemoFlag("chat");

/** Reveals the Reporting demo entry in the main Oasis nav. */
export const useReportingDemoFlag = createDemoFlag("reporting");

/**
 * The IHG Custom Buyer Briefs demo: the main-nav "Buyer Briefs" item points at
 * the IHG demo instead of the real buyer briefs.
 */
export const useIhgBriefsDemoFlag = createDemoFlag("ihgBriefs");

/** Shows the demo maker panel, for capturing slide decks while browsing. */
export const useDemoMakerFlag = createDemoFlag("demoMaker");

/** Reveals the Usage section (Pipeline and Portfolio) in the main Oasis nav. */
export const useUsageDemoFlag = createDemoFlag("usage");
